import req from '../../api/request'
import { ENDPOINTS } from '../../api/endpoints'
import type {
  FinderLocation,
  FinderPreference,
  FinderPreferenceInput,
  FinderSettings,
  TimeOfDay,
  WeekDay,
} from '../../features/profile/finderSettings/types'

// --- Real API response shape (finder-settings / finder-preferences) --------
interface ApiPlayerType {
  id: string | number
  name_full?: string | null
  name?: string | null
}

interface ApiFinderCompetition {
  id: string
  name?: string | null
}

interface ApiPlace {
  id: string | number
  name?: string | null
}

interface ApiFinderLocation {
  city?: string | null
  country?: ApiPlace | null
  province?: ApiPlace | null
}

interface ApiFinderPreference {
  id: string
  sport_id: number | string
  generic_sport_id?: number | string | null
  player_type?: ApiPlayerType | null
  skill_level?: string | null
  bio?: string | null
  charge_rate?: number | string | null
  available_days?: WeekDay[] | null
  available_times?: TimeOfDay[] | null
  is_public?: boolean
  /** SN-8299 — the competitions this sport's preference is saved for. */
  competitions_count?: number | null
  competitions?: ApiFinderCompetition[] | null
  /** SN-8295 — the card's own location; null when it has none. */
  location?: ApiFinderLocation | null
}

interface ApiFinderSettings {
  is_opted_in: boolean
  preferences?: ApiFinderPreference[] | null
}

const mapPlace = (place?: ApiPlace | null) =>
  place ? { id: Number(place.id), name: place.name ?? '' } : null

const mapLocation = (
  location?: ApiFinderLocation | null
): FinderLocation | null =>
  location
    ? {
        city: location.city ?? null,
        country: mapPlace(location.country),
        province: mapPlace(location.province),
      }
    : null

/**
 * Map one raw listing to the client `FinderPreference`. `sport_name` is left
 * blank here — it's resolved on the client from `sport_id` via the Redux sports
 * store (the API doesn't send a sport name).
 */
const mapApiPreference = (p: ApiFinderPreference): FinderPreference => ({
  id: p.id,
  sport_id: String(p.sport_id),
  generic_sport_id:
    p.generic_sport_id != null ? String(p.generic_sport_id) : '',
  sport_name: '',
  preferred_position: p.player_type ? String(p.player_type.id) : '',
  position_name: p.player_type?.name_full ?? p.player_type?.name ?? '',
  skill_level: p.skill_level ?? null,
  available_days: p.available_days ?? [],
  available_times: p.available_times ?? [],
  bio: p.bio ?? null,
  // API may send the rate as a number or numeric string; normalize to number.
  charge_rate:
    p.charge_rate != null && !Number.isNaN(Number(p.charge_rate))
      ? Number(p.charge_rate)
      : null,
  competitions_count: p.competitions_count ?? 1,
  competitions: (p.competitions ?? []).map((competition) => ({
    id: competition.id,
    name: competition.name ?? '',
  })),
  location: mapLocation(p.location),
})

/** One preference per sport (SN-8299), each saved for its competitions. */
const toFinderSettings = (api?: ApiFinderSettings | null): FinderSettings => ({
  is_opted_in: Boolean(api?.is_opted_in),
  preferences: (api?.preferences ?? []).map(mapApiPreference),
})

export const getFinderSettings = async (
  playerId: string
): Promise<FinderSettings> => {
  const { data } = await req(ENDPOINTS.PLAYERS.GET_FINDER_PREFERENCES(playerId))
  return toFinderSettings(data as ApiFinderSettings)
}

export const updateFinderOptIn = async (
  playerId: string,
  isOptedIn: boolean
): Promise<void> => {
  await req(ENDPOINTS.PLAYERS.UPDATE_FINDER_OPT_IN(playerId), {
    method: 'PUT',
    body: JSON.stringify({ is_opted_in: isOptedIn }),
  })
}

// SN-8299 — adding for a sport, editing and removing each return the whole
// settings envelope, so the caller can write it straight into the cache.

export const addFinderPreference = async (
  playerId: string,
  input: FinderPreferenceInput
): Promise<FinderSettings> => {
  const { data } = await req(
    ENDPOINTS.PLAYERS.ADD_FINDER_PREFERENCE(playerId),
    {
      method: 'POST',
      body: JSON.stringify(input),
    }
  )
  return toFinderSettings(data as ApiFinderSettings)
}

export const updateFinderPreference = async (
  playerId: string,
  prefId: string,
  input: FinderPreferenceInput
): Promise<FinderSettings> => {
  const { data } = await req(
    ENDPOINTS.PLAYERS.UPDATE_FINDER_PREFERENCE(playerId, prefId),
    { method: 'PUT', body: JSON.stringify(input) }
  )
  return toFinderSettings(data as ApiFinderSettings)
}

export const removeFinderPreference = async (
  playerId: string,
  prefId: string
): Promise<FinderSettings> => {
  const { data } = await req(
    ENDPOINTS.PLAYERS.REMOVE_FINDER_PREFERENCE(playerId, prefId),
    { method: 'DELETE' }
  )
  return toFinderSettings(data as ApiFinderSettings)
}
